import {
  HoverCard,
  HoverCardContent,
  HoverCardHeader,
  HoverCardTitle,
  HoverCardTrigger,
} from "~/components/reusable/hovercard/HoverCard"
import { cn } from "~/utils/helpers"

type Props = {
  /** Element the card hangs off. */
  trigger: React.ReactNode
  /** Card width, plus any content-specific classes. Padding is owned by the sections inside. */
  className?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Caps the card at the space the viewport has left and scrolls its content instead of overflowing. */
  scrollable?: boolean
  children: React.ReactNode
}

/**
 * Chrome shared by the breakdown hover cards: a top-anchored, padding-free card that holds its
 * position while the pointer travels from the trigger to the content.
 *
 * Legacy surface — the HoverCard family is not in design-system/APPROVED.md (see MER-622).
 */
export function BreakdownHoverCard({
  trigger,
  className,
  open,
  onOpenChange,
  scrollable,
  children,
}: Props) {
  return (
    <HoverCard closeDelay={600} open={open} onOpenChange={onOpenChange}>
      <HoverCardTrigger asChild>{trigger}</HoverCardTrigger>
      <HoverCardContent
        side="top"
        sticky="always"
        className={cn("p-0", className)}
        collisionPadding={16}
      >
        {scrollable ? (
          <div
            className="overflow-y-auto"
            style={{ maxHeight: "var(--radix-hover-card-content-available-height)" }}
          >
            {children}
          </div>
        ) : (
          children
        )}
      </HoverCardContent>
    </HoverCard>
  )
}

export function BreakdownHoverCardHeader({
  title,
  description,
}: {
  title: string
  description?: string
}) {
  return (
    <HoverCardHeader className="flex-col items-start h-auto">
      <HoverCardTitle>{title}</HoverCardTitle>
      {description && <p className="paragraph-sm text-fg-muted">{description}</p>}
    </HoverCardHeader>
  )
}
